import { Injectable } from '@angular/core';
import { catchError, first, map, mergeMap, startWith, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, EMPTY, iif, interval, Observable, of, throwError } from 'rxjs';
import { CommonAuthService } from '../common/services/auth.service';
import { CorporateClientService } from './services/corporate-client.service';
import { CorporateClient } from '../common/interfaces/corporate-client.interface';
import { Store, select } from '@ngrx/store';
import { SettingsState } from '../statemanagement/settings.state';
import { SetAppReady, UpdateShowFooter } from '../statemanagement/actions/settings.actions';
import { ActivatedRoute } from '@angular/router';
import { MyRouter } from '../common/services/router.service';
import { Title } from '@angular/platform-browser';
import { AppConfigService } from './services/app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { AvailabilityService } from './services/availability.service';
import { Availability } from '../common/interfaces/availability.interface';
import { UserState } from '../statemanagement/user.state';
import { UpdateLoggedIn, UpdatePreferredBranch, UpdateRights, UpdateUser } from '../statemanagement/actions/user.actions';
import { AuthenticationResult } from '../common/interfaces/authentication-result.interface';
import { ProductTranslationsService } from './services/product-translations.service';
import { CorporateClientState } from '../statemanagement/corporate-client.state';
import { UpdateCorporateClient } from '../statemanagement/actions/coprorate-client.actions';
import { CommonProfileService } from './services/profile.service';
import { Profile } from '../common/interfaces/profile.interface';
import { IntercomService } from './external-app-services/intercom/intercom.service';
import { RightsService } from './services/rights.service';

@Injectable()
export class AppSandbox {
    corporateClientName$: Observable<string>;
    allowedToOrder$: Observable<boolean>;
    showFooter$: Observable<boolean>;
    isOciUser$: Observable<boolean>;

    constructor(private commonAuthService: CommonAuthService,
                private commonProfileService: CommonProfileService,
                private corporateClientService: CorporateClientService,
                private translateService: TranslateService,
                private router: MyRouter,
                private activatedRoute: ActivatedRoute,
                private store: Store<{ settings: SettingsState, user: UserState, corporateClient: CorporateClientState }>,
                private titleService: Title,
                private appConfigService: AppConfigService,
                private availabilityService: AvailabilityService,
                private productTranslationsService: ProductTranslationsService,
                private intercomService: IntercomService,
                private rightsService: RightsService) {
        this.corporateClientName$ = store.pipe(
            select('corporateClient'),
            map((client: CorporateClientState) => client && client.corporateClientName));
        this.allowedToOrder$ = store.pipe(
            select('corporateClient'),
            map((client: CorporateClientState) => client && client.allowedToOrder),
            take(1));
        this.showFooter$ = store.pipe(
            select('settings'),
            map((settings: SettingsState) => settings && settings.showFooter));
        this.isOciUser$ = store.pipe(
            select('user'),
            map((user: UserState) => user && user.oci));
    }

    findAllCorporateClients(): Observable<CorporateClient[]> {
        return this.corporateClientService.findAll();
    }

    getStoreCorporateClientId(): Observable<number> {
        return this.store.pipe(
            select('corporateClient'),
            first(),
            map((client: CorporateClientState) => client && client.corporateClientId));
    }

    loadUserSettings() {
        return this.corporateClientService.findAll().pipe(
            mergeMap(clients => iif(() => clients.length > 0, this.setUserSettings(clients), of(EMPTY).pipe(tap(() => this.logout())))));
    }

    private setUserSettings(corporateClients) {
        return combineLatest([
            this.getStoreCorporateClientId(),
            this.activatedRoute.queryParams.pipe(take(1))
        ]).pipe(
            map(([currentCorporateClientId, params]) => {
                if (corporateClients.length > 0) {
                    let corporateClientId = null;
                    corporateClientId =
                        params['corporate-client'] ||
                        currentCorporateClientId ||
                        localStorage.getItem('corporate-client');

                    const findCorporateClient: CorporateClient = corporateClients.find((cl: CorporateClient) => cl.id === +corporateClientId);
                    let client: CorporateClient;
                    if (findCorporateClient) {
                        client = findCorporateClient;
                    } else if (corporateClients && corporateClients.length > 0 && corporateClients[0]) {
                        client = corporateClients[0];
                    } else {
                        this.logout();
                        return;
                    }

                    this.setCorporateClientSettingsToStore(client);
                }

                return corporateClients;
            }),
            switchMap(() => this.setPreferredBranchToStore()),
            switchMap(() => this.getProductTranslations()),
            switchMap(() => this.setRights()),
        );
    }

    setCorporateClientSettingsToStore(client: CorporateClient): void {
        this.store.dispatch(new UpdateShowFooter(true));
        this.store.dispatch(new UpdateCorporateClient({
            corporateClientId: client.id,
            corporateClientName: client.name,
            financiallyActive: client.financiallyActive,
            allowedToOrder: client.allowedToOrder,
            showPrices: client.showPrices,
        }));
        this.setBrowserTitle(client.name);
    }

    setPreferredBranchToStore(): Observable<void> {
        return this.commonProfileService.findProfile()
            .pipe(
                catchError(() => of({})),
                map((profile: Profile) => {
                    if (!!profile?.preferredBranchOfficeCode) {
                         this.store.dispatch(new UpdatePreferredBranch(profile?.preferredBranchOfficeCode));
                    }
                }),
            );
    }

    setAppReady() {
        this.store.dispatch(new SetAppReady());
    }

    getProductTranslations() {
        return this.productTranslationsService.findAll()
            .pipe(catchError(() => of({})));
    }

    loginUserWithToken(): Observable<any> {
        if (localStorage.getItem('token')) {
            const parts = localStorage.getItem('token').split('.');
            const result: AuthenticationResult = JSON.parse(atob(parts[1]));
            localStorage.setItem('userId', result?.userId);
            this.store.dispatch(new UpdateLoggedIn(true));
            this.store.dispatch(new UpdateUser({
                name: result?.firstName + ' ' + result?.lastName,
                email: result?.sub,
                userId: result?.userId
            }));
            return this.commonAuthService.loginUserWithToken();
        } else {
            return throwError(new Error('No token found'));
        }
    }

    logout() {
        localStorage.removeItem('token');
        this.intercomService.shutdown();
        this.store.dispatch(new UpdateUser({}));
        this.titleService.setTitle(this.appConfigService.getAppConfig()?.browserTitle);
        this.router.navigateToLogin();
    }

    getAvailability(): Observable<Availability> {
        return this.availabilityService.getAvailability();
    }

    setRights(): Observable<void> {
        return interval(3.6e+6)
            .pipe(
                startWith(0),
                mergeMap(() => this.rightsService.getRights().pipe(
                    map((rights: string[]) => this.store.dispatch(new UpdateRights(rights))))
            ));
    }

    private setBrowserTitle(clientName: string) {
        const title = clientName + ' - ' + this.appConfigService.getAppConfig().browserTitle;
        this.titleService.setTitle(title);
    }
}
